import { noteExcerpt } from "@/utils/content"
import { isReply, replyTargets } from "./feed"
import { parseReactionEvent, parseRepostEvent } from "./social"
import { KINDS, tagValue, type NostrEvent, type NostrFilter } from "../types"

export type NotificationType = "mention" | "reply" | "reaction" | "repost" | "follow"

export interface Notification {
  /** Unique key: one event can only produce one notification row. */
  key: string
  type: NotificationType
  event: NostrEvent
  actorPubkey: string
  createdAt: number
  /** Note this notification is about, when there is one. */
  targetEventId?: string
  preview: string
}

/** Filters for everything addressed to `pubkey`. */
export function buildNotificationFilters(pubkey: string, since?: number): NostrFilter[] {
  const base = { "#p": [pubkey] }
  const filters: NostrFilter[] = [
    { ...base, kinds: [KINDS.NOTE], limit: 100 },
    { ...base, kinds: [KINDS.REACTION, KINDS.REPOST], limit: 100 },
    { kinds: [KINDS.CONTACT_LIST, KINDS.RELAY_LIST], authors: [pubkey], limit: 2 },
  ]
  if (since) {
    for (const filter of filters.slice(0, 2)) filter.since = since
  }
  return filters
}

/** Turns a raw event into a notification row, or undefined when it is not one. */
export function parseNotification(event: NostrEvent, me: string): Notification | undefined {
  if (!event?.id || event.pubkey === me) return undefined
  const createdAt = event.created_at

  if (event.kind === KINDS.NOTE) {
    if (!event.tags.some((tag) => tag[0] === "p" && tag[1] === me)) return undefined
    const type: NotificationType = isReply(event) ? "reply" : "mention"
    return {
      key: `${type}:${event.id}`,
      type,
      event,
      actorPubkey: event.pubkey,
      createdAt,
      targetEventId: replyTargets(event).replyId,
      preview: noteExcerpt(event.content, 140),
    }
  }

  if (event.kind === KINDS.REACTION) {
    const { targetEventId } = parseReactionEvent(event)
    if (!targetEventId) return undefined
    return {
      key: `reaction:${event.id}`,
      type: "reaction",
      event,
      actorPubkey: event.pubkey,
      createdAt,
      targetEventId,
      preview: event.content.trim() || "+",
    }
  }

  if (event.kind === KINDS.REPOST) {
    const { targetEventId } = parseRepostEvent(event)
    if (!targetEventId) return undefined
    return {
      key: `repost:${event.id}`,
      type: "repost",
      event,
      actorPubkey: event.pubkey,
      createdAt,
      targetEventId,
      preview: "",
    }
  }

  if (event.kind === KINDS.CONTACT_LIST) {
    return {
      key: `follow:${event.pubkey}:${event.created_at}`,
      type: "follow",
      event,
      actorPubkey: event.pubkey,
      createdAt,
      preview: "",
    }
  }

  return undefined
}

/** Groups notifications by the note they refer to, keeping the newest first. */
export function groupNotifications(
  notifications: Notification[],
): Array<{ targetEventId: string; type: NotificationType; items: Notification[] }> {
  const groups = new Map<string, { targetEventId: string; type: NotificationType; items: Notification[] }>()
  for (const notification of notifications) {
    const target = notification.targetEventId ?? notification.event.id
    const key = `${notification.type}:${target}`
    const group = groups.get(key)
    if (group) {
      group.items.push(notification)
    } else {
      groups.set(key, { targetEventId: target, type: notification.type, items: [notification] })
    }
  }
  return [...groups.values()]
    .map((group) => ({
      ...group,
      items: group.items.sort((a, b) => b.createdAt - a.createdAt),
    }))
    .sort((a, b) => b.items[0].createdAt - a.items[0].createdAt)
}

export const NOTIFICATION_LABELS: Record<NotificationType, string> = {
  mention: "mentioned you",
  reply: "replied to your note",
  reaction: "reacted to your note",
  repost: "reposted your note",
  follow: "followed you",
}

/** The last time the user opened the notifications page (persisted). */
export function notificationTargetId(event: NostrEvent): string | undefined {
  if (event.kind === KINDS.NOTE) return event.id
  return tagValue(event, "e")
}
